import React, {useState, useEffect, useContext} from 'react'

import moment from 'moment'

import DataContext from '../../DataContext'
import EffortTile from './EffortTile';
import EffortMonthMarker from './EffortMonthMarker'

const Efforts = () => {

    const {data} = useContext(DataContext);
    const [tiles, setTiles] = useState([]);
    const [monthMarkers, setMonthMarkers] = useState([]);
    const [pxPerDay, setPxPerDay] = useState(50);
    const [maxDowntime, setMaxDowntime] = useState(10);
    const [maxHeight, setMaxHeight] = useState(1000);

    useEffect(() => {
        window.addEventListener("keydown", e => {
            if(e.key === "ArrowLeft") setMaxDowntime(cur => Math.max(1, cur - 1));
            else if(e.key === "ArrowRight") setMaxDowntime(cur => cur + 1);
        })
    }, [])

    //Note - this is a temporary method that does NOT utilize existing efforts!
    //This is a test of a hypothetical system by whch Efforts are auto-generated from entry labels and timestamps
    useEffect(() => {
        let efforts = [];
        let currentEffort = null;
        data.projects.forEach((project, pIndex) => {
            currentEffort = null;
            project.entries.map(e => data.entries[e.index]).forEach(entry => {
                let needsNew = false;
                //if this is the first entry, we need a new effort
                if(!currentEffort) needsNew = true; 
                else {
                    //if the entry has changed, we need a new effort
                    if(entry.label !== currentEffort.type) needsNew = true;
                    //if five days have passed since the last entry, we need a new effort
                    const tsA = moment(currentEffort.entries.slice(-1)[0].timestamp, "x");
                    const tsB = moment(entry.timestamp, "x");
                    const downtime = Math.abs(tsA.diff(tsB, "days"));
                    if(downtime > maxDowntime) needsNew = true;
                }

                if(needsNew) {
                    if(currentEffort) {
                        currentEffort.endDate = Number(currentEffort.entries.slice(-1)[0].timestamp);
                        efforts.push(currentEffort);
                    }
                    currentEffort = {
                        id: entry.id,
                        startDate: Number(entry.timestamp),
                        type: entry.label,
                        summary: entry.summary,
                        project: project,
                        entries: [entry],
                    };
                } else {
                    currentEffort.entries.push(entry);
                }
            })

            if(currentEffort && efforts.findIndex(e => e.id === currentEffort.id) === -1) {
                //add the last effort
                currentEffort.endDate = Number(currentEffort.entries.slice(-1)[0].timestamp)
                efforts.push(currentEffort);
            }
        });

        efforts = efforts.map(effort => {
            const newEntries = effort.entries.sort((a, b) => b.timestamp - a.timestamp);
            const start = Number(newEntries.slice(-1)[0].timestamp);
            const end = Number(newEntries[0].timestamp);
            return {
                ...effort, 
                entries: newEntries,
                startDate: Math.min(start, end),
                startDaysAgo: moment().diff(moment(Math.min(start, end), "x"), "days"),
                endDate: Math.max(start, end),
                endDaysAgo: moment().diff(moment(Math.max(start, end), "x"), "days"),
            }
        })

        //Sort efforts to determine the initial offset
        efforts = efforts.sort((a, b) => b.startDate - a.startDate);
        //const dayOffset = efforts[0].endDaysAgo;
        const dayOffset = 0;

        //Determine start and end position for efforts
        efforts = efforts.map(effort => {
            const top = (effort.endDaysAgo - dayOffset) * pxPerDay;
            const height = Math.max(pxPerDay, Math.abs(effort.endDaysAgo - effort.startDaysAgo) * pxPerDay);
            return {...effort, top, height}
        });

        efforts.forEach(effort => {
            if(efforts.filter(e => e.id === effort.id).length > 1) {
                console.warn("Oh no multiple efforts have an ID of " + effort.id);
            }
        })

        //sort efforts by start day for concurrency calculations
        efforts = efforts.sort((a, b) => b.startDaysAgo - a.startDaysAgo);

        //also, for the purpose of concurrency, we subtract one from the start date of all single-day efforts
        //since they're going to extend down one 'day' anyway
        efforts = efforts.map(effort => effort.endDaysAgo - effort.startDaysAgo !== 0 
            ? effort 
            : {...effort, startDaysAgo: effort.startDaysAgo + 1});

        //Work out how many efforts before this one are still ongoing, and thus how far to the right it should be
        const maxDaysAgo = efforts.reduce((acc, effort) => Math.max(acc, effort.startDaysAgo), 0);
        const days = [];
        for(let i = 0; i < maxDaysAgo; i++) days.push(0);

        //set effort name to the FIRST entry, not the last
        efforts = efforts.map(effort => ({...effort, summary: effort.entries.slice(-1)[0].summary}));

        let maxConcurrency = 0;
        efforts.forEach((effort, index) => {
            const dayConcurrency = days
                .filter((day, i) => effort.endDaysAgo <= i && effort.startDaysAgo >= i)
                .reduce((acc, day) => Math.max(acc, day), 0);

            let effortConcurrency = 0;
            //we only need to check up to day concurrency when determining which column to place the effort
            for(let i = 0; i < dayConcurrency + 1; i++) {
                effortConcurrency = i;
                let taken = false;
                //for each day already placed
                for(let j = 0; j < index; j++) {
                    if(efforts[j].endDaysAgo >= effort.startDaysAgo) continue;

                    if(efforts[j].concurrency === i) {
                        //this column is taken
                        taken = true;
                        break;
                    }
                }
                if(taken) continue;
                break;
            }
            effort.concurrency = effortConcurrency;
            maxConcurrency = Math.max(maxConcurrency, effort.concurrency);

            for(let i = effort.endDaysAgo; i <= effort.startDaysAgo - 1; i++) {
                days[i]++;
            }
        })

        const monthCount = Math.abs(moment().diff(moment(efforts[0].startDate, "x"), "months"));
        const months = [];
        for(let i = 0; i < monthCount; i++) {
            months.push(moment().startOf("month").subtract(i, "months"));
        }
        setMonthMarkers(months.map(month => <EffortMonthMarker 
                                                key={month.valueOf()} 
                                                timestamp={month.valueOf()}
                                                top={(moment().diff(month, "days") - dayOffset) * pxPerDay}
                                            />));

        setMaxHeight((efforts[0].startDaysAgo - dayOffset) * pxPerDay);

        setTiles(efforts.map(effort => {
            return <EffortTile 
                key={effort.id}
                effort={effort}
                top={effort.top}
                height={effort.height}
                left={effort.concurrency * (100 / maxConcurrency)}
                width={100 / maxConcurrency}
                pxPerDay={pxPerDay}
            />
        }))
    }, [data.projects, data.entries, maxDowntime, pxPerDay])

    return (
        <div>
            <div className="container" style={{
                height: maxHeight + "px"
            }}>
                {
                /* debug controls, turned off for now*/
                true ? null : ( 
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        margin: "1em 0"
                    }}>
                        <div>
                            <label>Pixels Per Day</label>
                            <input type="number" value={pxPerDay} onChange={e => setPxPerDay(e.target.value)} />
                        </div>
                        <div>
                            <label>Max Downtime</label>
                            <input type="number" value={maxDowntime} onChange={e => setMaxDowntime(e.target.value)} />
                        </div>
                    </div>
                )}
                <div style={{position: "relative"}}>
                    {tiles}
                </div>
            </div>
            <div style={{position: "absolute", top: "0px", width: "100vw"}}>
                {monthMarkers}
            </div>
        </div>
    )
}

export default Efforts;